/**
 * Format service-specific details for admin display
 * Handles photo_restoration, lora_training, photo_scanning
 */

// Human-readable labels for service types
export const SERVICE_TYPE_LABELS = {
  photo_restoration: 'Photo Restoration',
  lora_training: 'LoRA Training',
  photo_scanning: 'Photo Scanning',
  digital_persona: 'Digital Persona',
  general_upload: 'General Upload',
};

// Format photo restoration details
function formatPhotoRestorationDetails(details = {}) {
  return [
    {
      label: 'Photo Count',
      value: details.photoCount || details.photo_count || '—',
    },
    {
      label: 'Restoration Level',
      value: formatRestorationLevel(details.restorationLevel || details.restoration_level),
    },
    {
      label: 'Colorization',
      value: formatBoolean(details.colorization),
    },
    {
      label: 'Output Format',
      value: details.outputFormat || details.output_format || '—',
    },
    {
      label: 'Restoration Notes',
      value: details.restorationNotes || details.restoration_notes || '—',
    },
  ].filter(item => item.value !== '—' || item.label === 'Photo Count');
}

// Format LoRA training details
function formatLoraTrainingDetails(details = {}) {
  return [
    {
      label: 'Package',
      value: formatLoraPackage(details.package),
    },
    {
      label: 'Number of Images',
      value: details.imageCount || details.image_count || details.numberOfImages || '—',
    },
    {
      label: 'Style',
      value: details.style || '—',
    },
    {
      label: 'Style Notes',
      value: details.styleNotes || details.style_notes || '—',
    },
    {
      label: 'Subject Type',
      value: details.subjectType || details.subject_type || '—',
    },
    {
      label: 'Deliverables',
      value: formatDeliverables(details.deliverables),
    },
    {
      label: 'Trigger Word',
      value: details.triggerWord || details.trigger_word || '—',
    },
  ].filter(item => item.value !== '—' || item.label === 'Package');
}

// Format photo scanning details
function formatPhotoScanningDetails(details = {}) {
  return [
    {
      label: 'Quantity Range',
      value: details.quantityRange || details.quantity_range || details.quantity || '—',
    },
    {
      label: 'Media Type',
      value: formatMediaType(details.mediaType || details.media_type),
    },
    {
      label: 'DPI',
      value: details.dpi || details.resolution || '—',
    },
    {
      label: 'Color Mode',
      value: formatColorMode(details.colorMode || details.color_mode),
    },
    {
      label: 'Return Method',
      value: formatReturnMethod(details.returnMethod || details.return_method),
    },
    {
      label: 'Special Instructions',
      value: details.specialInstructions || details.special_instructions || '—',
    },
  ].filter(item => item.value !== '—' || item.label === 'Quantity Range');
}

// Helper formatters
function formatRestorationLevel(level) {
  if (!level) return '—';
  const levels = {
    basic: 'Basic Cleanup',
    standard: 'Standard Restoration',
    advanced: 'Advanced Restoration',
    premium: 'Premium (Heavy Damage)',
  };
  return levels[level] || level;
}

function formatLoraPackage(pkg) {
  if (!pkg) return '—';
  const packages = {
    starter: 'Starter (10 images)',
    standard: 'Standard (20 images)',
    premium: 'Premium (40 images)',
    professional: 'Professional (80+ images)',
  };
  return packages[pkg] || pkg;
}

function formatDeliverables(deliverables) {
  if (!deliverables) return '—';
  if (Array.isArray(deliverables)) {
    return deliverables.length > 0 ? deliverables.join(', ') : '—';
  }
  return String(deliverables);
}

function formatMediaType(type) {
  if (!type) return '—';
  const types = {
    photos: 'Photos',
    slides: 'Slides',
    negatives: 'Negatives',
    mixed: 'Mixed Media',
  };
  return types[type] || type;
}

function formatColorMode(mode) {
  if (!mode) return '—';
  const modes = {
    color: 'Color',
    bw: 'Black & White',
    'black-white': 'Black & White',
    mixed: 'Mixed',
  };
  return modes[mode] || mode;
}

function formatReturnMethod(method) {
  if (!method) return '—';
  const methods = {
    mail: 'Return by Mail',
    pickup: 'Local Pickup',
    digital_only: 'Digital Only (No Return)',
    'keep-originals': 'Digital Only',
  };
  return methods[method] || method;
}

function formatBoolean(value) {
  if (value === undefined || value === null) return '—';
  if (value === true || value === 'yes' || value === 'true') return 'Yes';
  if (value === false || value === 'no' || value === 'false') return 'No';
  return String(value);
}

/**
 * Main formatter function
 * @param {string} serviceType - The service type (photo_restoration, lora_training, photo_scanning)
 * @param {object} details - The order details object
 * @returns {Array<{label: string, value: string}>} Array of formatted key-value pairs
 */
export function formatServiceDetails(serviceType, details = {}) {
  if (!details || Object.keys(details).length === 0) {
    return [{ label: 'Intake Details', value: 'No details provided' }];
  }

  switch (serviceType) {
    case 'photo_restoration':
      return formatPhotoRestorationDetails(details);
    case 'lora_training':
      return formatLoraTrainingDetails(details);
    case 'photo_scanning':
      return formatPhotoScanningDetails(details);
    default:
      // For unknown service types, display raw key-value pairs
      return Object.entries(details)
        .filter(([_, value]) => value !== null && value !== undefined && value !== '')
        .map(([key, value]) => ({
          label: key.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').trim(),
          value: typeof value === 'object' ? JSON.stringify(value) : String(value),
        }));
  }
}

/**
 * Format currency amount from cents
 * @param {number} cents - Amount in cents
 * @param {string} currency - Currency code (default: USD)
 * @returns {string} Formatted currency string
 */
export function formatCurrency(cents, currency = 'USD') {
  if (cents === null || cents === undefined) return '—';
  const amount = cents / 100;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(amount);
}

/**
 * Format payment state for display
 * @param {string} state - Payment state
 * @returns {string} Human-readable payment state
 */
export function formatPaymentState(state) {
  if (!state) return '—';
  const states = {
    unpaid: 'Unpaid',
    pending: 'Payment Pending',
    paid: 'Paid',
    failed: 'Payment Failed',
    refunded: 'Refunded',
  };
  return states[state] || state;
}

export default formatServiceDetails;
